package com.sweetlime.dungeonamigo.presentation.ui.components

import androidx.compose.animation.core.*
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.*
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.sweetlime.dungeonamigo.model.DiceType
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.sqrt

@Composable
fun DiceCanvas(
    diceType: DiceType,
    value: Int,
    isAnimating: Boolean,
    isSelected: Boolean,
    isDropped: Boolean = false,
    modifier: Modifier = Modifier,
    size: Dp = 64.dp,
) {
    val isCritSuccess = diceType == DiceType.D20 && value == 20 && !isAnimating && value > 0
    val isCritFail = diceType == DiceType.D20 && value == 1 && !isAnimating && value > 0

    val fillColor = when {
        isDropped -> BrandColors.Surface
        isCritSuccess -> Color(0xFFFFD700L)
        isCritFail -> BrandColors.Ember
        isSelected || isAnimating -> BrandColors.SurfaceLight
        else -> BrandColors.Surface
    }

    val strokeColor = when {
        isDropped -> BrandColors.TextSecondary.copy(alpha = 0.4f)
        isCritSuccess -> Color(0xFFFFD700L)
        isCritFail -> BrandColors.Ember
        isSelected || isAnimating -> BrandColors.Parchment
        else -> BrandColors.TextSecondary
    }

    val textColor = when {
        isDropped -> BrandColors.TextSecondary.copy(alpha = 0.4f)
        isCritSuccess || isCritFail -> Color.White
        isSelected || isAnimating -> BrandColors.Parchment
        else -> BrandColors.TextSecondary
    }

    val strokeWidth = if (isSelected || isCritSuccess || isCritFail) 2.5.dp else 1.5.dp

    // Pulsing alpha for animating stroke
    val infiniteTransition = rememberInfiniteTransition(label = "dice_anim")
    val pulseAlpha by infiniteTransition.animateFloat(
        initialValue = 0.6f,
        targetValue = 1f,
        animationSpec = infiniteRepeatable(
            animation = tween(500, easing = LinearEasing),
            repeatMode = RepeatMode.Reverse,
        ),
        label = "pulse",
    )

    // Scale for crit success
    val scale by animateFloatAsState(
        targetValue = if (isCritSuccess) 1.15f else 1f,
        animationSpec = spring(stiffness = Spring.StiffnessMedium),
        label = "crit_scale",
    )

    // Shake for crit fail
    var shakeX by remember { mutableStateOf(0f) }
    val scope = rememberCoroutineScope()
    LaunchedEffect(isCritFail) {
        if (isCritFail) {
            scope.launch {
                repeat(6) { i ->
                    shakeX = if (i % 2 == 0) 5f else -5f
                    delay(80)
                }
                shakeX = 0f
            }
        }
    }
    val shakeOffset by animateFloatAsState(
        targetValue = shakeX,
        animationSpec = spring(stiffness = Spring.StiffnessHigh),
        label = "shake",
    )

    val density = LocalDensity.current
    val sizePx = with(density) { size.toPx() }
    val strokeWidthPx = with(density) { strokeWidth.toPx() }
    val effectiveStrokeAlpha = if (isAnimating) pulseAlpha else 1f
    val overallAlpha = if (isDropped) 0.4f else 1f

    Box(
        modifier = modifier
            .size(size)
            .offset(x = shakeOffset.dp)
            .graphicsLayer { scaleX = scale; scaleY = scale },
        contentAlignment = if (diceType == DiceType.D4) Alignment.BottomCenter else Alignment.Center,
    ) {
        Canvas(modifier = Modifier.fillMaxSize()) {
            val cx = sizePx / 2f
            val cy = sizePx / 2f
            val radius = sizePx * 0.38f

            if (diceType == DiceType.D100) {
                drawCircle(
                    color = fillColor.copy(alpha = overallAlpha),
                    radius = radius,
                    center = Offset(cx, cy),
                )
                drawCircle(
                    color = strokeColor.copy(alpha = overallAlpha * effectiveStrokeAlpha),
                    radius = radius,
                    center = Offset(cx, cy),
                    style = Stroke(width = strokeWidthPx),
                )
                if (isCritSuccess) {
                    drawCircle(
                        color = Color(0xFFFFD700L).copy(alpha = 0.3f),
                        radius = radius * 1.3f,
                        center = Offset(cx, cy),
                    )
                }
            } else {
                val path = buildDicePath(diceType, cx, cy, radius)
                drawPath(path = path, color = fillColor.copy(alpha = overallAlpha))
                drawPath(
                    path = path,
                    color = strokeColor.copy(alpha = overallAlpha * effectiveStrokeAlpha),
                    style = Stroke(width = strokeWidthPx),
                )
                if (isCritSuccess) {
                    val glowPath = buildDicePath(diceType, cx, cy, radius * 1.3f)
                    drawPath(path = glowPath, color = Color(0xFFFFD700L).copy(alpha = 0.2f))
                }
            }
        }

        if (value > 0 || isAnimating) {
            val d4YOffset = if (diceType == DiceType.D4) (-sizePx * 0.15f / density.density).dp else 0.dp
            val displayText = if (diceType == DiceType.D100) "${value}%" else "$value"
            Text(
                text = displayText,
                color = textColor.copy(alpha = overallAlpha),
                fontSize = (size.value * 0.30f).sp,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.offset(y = d4YOffset),
            )
        } else {
            Text(
                text = diceType.label,
                color = (if (isSelected) BrandColors.Parchment else BrandColors.TextSecondary).copy(alpha = overallAlpha),
                fontSize = (size.value * 0.22f).sp,
                fontWeight = FontWeight.SemiBold,
            )
        }
    }
}

private fun DrawScope.buildDicePath(
    diceType: DiceType,
    cx: Float,
    cy: Float,
    radius: Float,
): Path = when (diceType) {
    DiceType.D4  -> trianglePath(cx, cy, radius)
    DiceType.D6  -> squarePath(cx, cy, radius)
    DiceType.D8  -> diamondPath(cx, cy, radius)
    DiceType.D10 -> kitePath(cx, cy, radius)
    DiceType.D12 -> regularPolygonPath(cx, cy, radius, 5, -PI / 2)
    DiceType.D20 -> regularPolygonPath(cx, cy, radius, 6, 0.0)
    DiceType.D100 -> Path() // handled separately
}

private fun trianglePath(cx: Float, cy: Float, radius: Float): Path {
    val h = radius * sqrt(3f) / 2f
    return Path().apply {
        moveTo(cx, cy - radius * 0.85f)
        lineTo(cx + h, cy + radius * 0.5f)
        lineTo(cx - h, cy + radius * 0.5f)
        close()
    }
}

private fun squarePath(cx: Float, cy: Float, radius: Float): Path {
    val half = radius * 0.82f
    return Path().apply {
        val r = half * 0.15f
        moveTo(cx - half + r, cy - half)
        lineTo(cx + half - r, cy - half)
        quadraticTo(cx + half, cy - half, cx + half, cy - half + r)
        lineTo(cx + half, cy + half - r)
        quadraticTo(cx + half, cy + half, cx + half - r, cy + half)
        lineTo(cx - half + r, cy + half)
        quadraticTo(cx - half, cy + half, cx - half, cy + half - r)
        lineTo(cx - half, cy - half + r)
        quadraticTo(cx - half, cy - half, cx - half + r, cy - half)
        close()
    }
}

private fun diamondPath(cx: Float, cy: Float, radius: Float): Path {
    return Path().apply {
        moveTo(cx, cy - radius)
        lineTo(cx + radius * 0.7f, cy)
        lineTo(cx, cy + radius)
        lineTo(cx - radius * 0.7f, cy)
        close()
    }
}

private fun kitePath(cx: Float, cy: Float, radius: Float): Path {
    return Path().apply {
        moveTo(cx, cy - radius)
        lineTo(cx + radius * 0.75f, cy - radius * 0.08f)
        lineTo(cx + radius * 0.42f, cy + radius)
        lineTo(cx - radius * 0.42f, cy + radius)
        lineTo(cx - radius * 0.75f, cy - radius * 0.08f)
        close()
    }
}

private fun regularPolygonPath(
    cx: Float,
    cy: Float,
    radius: Float,
    sides: Int,
    startAngle: Double,
): Path {
    return Path().apply {
        for (i in 0 until sides) {
            val angle = startAngle + 2 * PI * i / sides
            val x = (cx + radius * cos(angle)).toFloat()
            val y = (cy + radius * sin(angle)).toFloat()
            if (i == 0) moveTo(x, y) else lineTo(x, y)
        }
        close()
    }
}
